// The visual editor: the parsed form model rendered as editable cards
// (pages, questions, content blocks). Edits mutate the model and call
// onChange(); the builder serializes the model back to text. Simple field
// edits update in place (the DOM is kept, focus stays); structural changes
// (add, move, delete, type change) ask the builder to rebuild the cards.
import { COUNTRY_CODES, QUESTION_TYPES, countryName, flagEmoji, phoneCode, formatSize, parseSize, slugify, uniqueId } from '@askdown/core';
import { h, icon, clear } from './dom.js';

const TYPE_LABELS = {
  text: 'Short text',
  paragraph: 'Paragraph',
  email: 'Email',
  url: 'URL',
  phone: 'Phone number',
  number: 'Number',
  date: 'Date',
  time: 'Time',
  radio: 'Single choice',
  checkbox: 'Multiple choice',
  select: 'Dropdown',
  scale: 'Linear scale',
  rating: 'Rating',
  grid: 'Grid (single per row)',
  'checkbox-grid': 'Grid (multiple per row)',
  file: 'File upload',
  address: 'Address',
  ranking: 'Ranking',
};

const COUNTRY_OPTIONS = COUNTRY_CODES.map((code) => ({ code, name: countryName(code) })).sort((a, b) => a.name.localeCompare(b.name));

const CHOICE = new Set(['radio', 'checkbox', 'select']);

export function typeLabel(type) {
  return TYPE_LABELS[type] || type;
}

/** Copies a grid row/column ({label, value?, flags?}); tolerates plain strings. */
function copyCell(c) {
  if (typeof c === 'string') return { label: c };
  const out = { label: c.label };
  if (c.value !== undefined) out.value = c.value;
  if (c.flags && c.flags.length) out.flags = [...c.flags];
  return out;
}

/** Builds a question of `type` from an existing question's shared fields. */
export function convertQuestion(q, type) {
  const next = { id: q.id, type, label: q.label };
  if (q.help) next.help = q.help;
  if (q.required) next.required = true;
  switch (type) {
    case 'radio':
    case 'checkbox':
    case 'select':
    case 'ranking': {
      const options = Array.isArray(q.options) && q.options.length ? q.options : [{ label: 'Option 1' }, { label: 'Option 2' }];
      next.options = options.map((o) => {
        const opt = { label: o.label };
        if (o.value !== undefined) opt.value = o.value;
        if (o.flags && o.flags.length) opt.flags = [...o.flags];
        if (type === 'ranking') return opt;
        if (o.default) opt.default = true;
        if (o.goto && type !== 'checkbox') opt.goto = o.goto;
        return opt;
      });
      if (type === 'radio' || type === 'select') {
        let seen = false;
        for (const o of next.options) {
          if (o.default && seen) delete o.default;
          if (o.default) seen = true;
        }
      }
      if (q.other !== undefined && (type === 'radio' || type === 'checkbox')) next.other = q.other;
      if (q.shuffle) next.shuffle = true;
      break;
    }
    case 'scale':
      next.min = Number.isInteger(q.min) ? q.min : 1;
      next.max = Number.isInteger(q.max) && q.max > next.min ? q.max : 5;
      if (q.minLabel) next.minLabel = q.minLabel;
      if (q.maxLabel) next.maxLabel = q.maxLabel;
      break;
    case 'rating':
      next.max = Number.isInteger(q.max) && q.max >= 2 && q.max <= 10 ? q.max : 5;
      break;
    case 'grid':
    case 'checkbox-grid':
      next.rows = Array.isArray(q.rows) && q.rows.length ? q.rows.map(copyCell) : [{ label: 'Row 1' }, { label: 'Row 2' }];
      next.columns = Array.isArray(q.columns) && q.columns.length ? q.columns.map(copyCell) : [{ label: 'Column 1' }, { label: 'Column 2' }];
      break;
    case 'text':
    case 'paragraph':
      if (q.placeholder) next.placeholder = q.placeholder;
      if (q.minLength !== undefined) next.minLength = q.minLength;
      if (q.maxLength !== undefined) next.maxLength = q.maxLength;
      break;
    case 'email':
    case 'url':
    case 'number':
      if (q.placeholder) next.placeholder = q.placeholder;
      break;
    case 'phone':
      if (q.placeholder) next.placeholder = q.placeholder;
      if (q.country) next.country = q.country;
      break;
    case 'address':
      if (q.country) next.country = q.country;
      break;
    default:
      break;
  }
  return next;
}

export function newQuestion(type, label, usedIds) {
  const base = { id: uniqueId(slugify(label), usedIds), type: 'text', label };
  return convertQuestion(base, type);
}

/**
 * @param {HTMLElement} container
 * @param {{
 *   getForm: () => import('@askdown/core').Form,
 *   getLocations: () => { questions: Record<string, {explicitId: boolean}>, pages: Record<string, {explicitId: boolean}> },
 *   onChange: (options: { structural: boolean }) => void,
 * }} options
 */
export function createVisual(container, options) {
  const root = h('div.visual');
  container.append(root);

  const form = () => options.getForm();
  const allQuestionIds = () => new Set(form().pages.flatMap((p) => p.items.filter((i) => i.type !== 'content').map((i) => i.id)));
  const allPageIds = () => new Set(form().pages.map((p) => p.id));

  let counter = 0;
  const uid = (prefix) => `${prefix}-${++counter}`;

  function emit(structural = false) {
    options.onChange({ structural });
  }

  // --- Field helpers -------------------------------------------------------

  function field(labelText, control, { hint, inline = false, name } = {}) {
    const id = control.id || uid('vf');
    control.id = id;
    if (name) control.dataset.field = name;
    const wrapper = h(inline ? 'div.vf.vf--inline' : 'div.vf');
    const label = h('label.vf-label', { for: id }, labelText);
    if (inline) wrapper.append(control, label);
    else wrapper.append(label, control);
    if (hint) wrapper.append(h('span.vf-hint', hint));
    return wrapper;
  }

  function textInput(get, set, { placeholder = '', type = 'text', name } = {}) {
    const input = h('input.vf-input', { type, placeholder, value: get() ?? '' });
    if (name) input.dataset.field = name;
    input.addEventListener('input', () => {
      set(input.value);
      emit(false);
    });
    return input;
  }

  function optionalText(obj, key, opts) {
    return textInput(
      () => obj()[key] ?? '',
      (v) => {
        const o = obj();
        if (v === '') delete o[key];
        else o[key] = v;
      },
      opts,
    );
  }

  function optionalNumber(obj, key, { integer = false, min, max, step, placeholder = '', name } = {}) {
    const input = h('input.vf-input.vf-input--num', {
      type: 'number',
      placeholder,
      value: obj()[key] ?? '',
      min,
      max,
      step: step ?? (integer ? 1 : 'any'),
      inputmode: integer ? 'numeric' : 'decimal',
    });
    if (name) input.dataset.field = name;
    input.addEventListener('input', () => {
      const o = obj();
      const n = input.value === '' ? NaN : Number(input.value);
      if (Number.isFinite(n) && (!integer || Number.isInteger(n))) o[key] = n;
      else delete o[key];
      emit(false);
    });
    return input;
  }

  function requiredNumber(obj, key, { min, max, fallback, name } = {}) {
    const input = h('input.vf-input.vf-input--num', { type: 'number', value: obj()[key], min, max, step: 1, inputmode: 'numeric' });
    if (name) input.dataset.field = name;
    input.addEventListener('input', () => {
      const n = Number(input.value);
      obj()[key] = Number.isInteger(n) ? n : fallback;
      emit(false);
    });
    return input;
  }

  function checkbox(get, set, { name } = {}) {
    const input = h('input.vf-check', { type: 'checkbox', checked: Boolean(get()) });
    if (name) input.dataset.field = name;
    input.addEventListener('change', () => {
      set(input.checked);
      emit(false);
    });
    return input;
  }

  function textarea(get, set, { rows = 2, placeholder = '', name, structuralOnBlur = false } = {}) {
    const ta = h('textarea.vf-input.vf-textarea', { rows, placeholder, value: get() ?? '' });
    if (name) ta.dataset.field = name;
    ta.addEventListener('input', () => {
      set(ta.value);
      emit(false);
      autosize(ta);
    });
    requestAnimationFrame(() => autosize(ta));
    return ta;
  }

  function autosize(ta) {
    ta.style.height = 'auto';
    ta.style.height = `${Math.min(ta.scrollHeight + 2, 400)}px`;
  }

  function iconButton(name, title, onClick, extraClass = '') {
    return h('button.vbtn' + (extraClass ? '.' + extraClass : ''), { type: 'button', title, 'aria-label': title, onClick }, icon(name));
  }

  function pageOptions(currentPageId, includeContinue = true) {
    const opts = [];
    if (includeContinue) opts.push(h('option', { value: '' }, 'Continue to next page'));
    else opts.push(h('option', { value: '' }, 'No jump'));
    opts.push(h('option', { value: 'submit' }, 'Submit the form'));
    for (const p of form().pages) {
      if (p.id === currentPageId) continue;
      opts.push(h('option', { value: p.id }, `Go to: ${p.title || p.id}`));
    }
    return opts;
  }

  // --- Rename helpers ------------------------------------------------------

  function maybeRederiveQuestionId(q, idInput) {
    const loc = options.getLocations().questions[q.id];
    if (loc && loc.explicitId) return;
    const used = allQuestionIds();
    used.delete(q.id);
    const next = uniqueId(slugify(q.label), used);
    if (next !== q.id) {
      q.id = next;
      if (idInput) idInput.value = next;
    }
  }

  function renamePage(page, newId) {
    if (!newId || newId === page.id) return;
    const old = page.id;
    page.id = newId;
    for (const p of form().pages) {
      if (p.next === old) p.next = newId;
      for (const item of p.items) {
        if (item.type === 'content' || !CHOICE.has(item.type)) continue;
        for (const o of item.options) if (o.goto === old) o.goto = newId;
      }
    }
  }

  function maybeRederivePageId(page, idInput, index) {
    const loc = options.getLocations().pages[page.id];
    if (loc && loc.explicitId) return;
    const used = allPageIds();
    used.delete(page.id);
    const next = uniqueId(page.title ? slugify(page.title, 'page') : `page-${index + 1}`, used);
    if (next !== page.id) {
      renamePage(page, next);
      if (idInput) idInput.value = next;
    }
  }

  // --- Cards ---------------------------------------------------------------

  function questionCard(pi, ii) {
    const q = () => form().pages[pi].items[ii];
    const card = h('article.vcard.vcard--question', { dataset: { path: `${pi}.${ii}` } });

    const typeSelect = h('select.vf-select.vf-type', { 'aria-label': 'Question type', dataset: { field: 'type' } });
    for (const t of QUESTION_TYPES) typeSelect.append(h('option', { value: t, selected: t === q().type }, typeLabel(t)));
    typeSelect.addEventListener('change', () => {
      const page = form().pages[pi];
      page.items[ii] = convertQuestion(page.items[ii], typeSelect.value);
      emit(true);
    });

    const requiredBox = checkbox(
      () => q().required,
      (v) => {
        if (v) q().required = true;
        else delete q().required;
      },
      { name: 'required' },
    );

    const head = h(
      'header.vcard-head',
      h('span.vcard-kind', icon('question'), `Question`),
      typeSelect,
      field('Required', requiredBox, { inline: true }),
      h(
        'div.vcard-tools',
        iconButton('up', 'Move up', () => moveItem(pi, ii, -1)),
        iconButton('down', 'Move down', () => moveItem(pi, ii, 1)),
        iconButton('duplicate', 'Duplicate', () => duplicateItem(pi, ii)),
        iconButton('trash', 'Delete question', () => deleteItem(pi, ii), 'vbtn--danger'),
      ),
    );

    const idInput = textInput(
      () => q().id,
      (v) => {
        const clean = v.trim();
        if (clean) q().id = clean;
      },
      { name: 'id' },
    );
    idInput.classList.add('vf-input--mono');
    const labelInput = textInput(
      () => q().label,
      (v) => {
        q().label = v;
        maybeRederiveQuestionId(q(), idInput);
      },
      { placeholder: 'Question', name: 'label' },
    );
    labelInput.classList.add('vf-input--label');

    const body = h(
      'div.vcard-body',
      field('Label', labelInput),
      h(
        'div.vf-row',
        field(
          'Help text',
          textarea(
            () => q().help,
            (v) => {
              if (v.trim() === '') delete q().help;
              else q().help = v;
            },
            { rows: 1, placeholder: 'Shown under the label (Markdown)', name: 'help' },
          ),
        ),
        field('Id', idInput, { hint: 'Answers are stored under this id.' }),
      ),
      typeFields(pi, ii),
    );
    card.append(head, body);
    return card;
  }

  function typeFields(pi, ii) {
    const q = () => form().pages[pi].items[ii];
    const type = q().type;
    const wrap = h('div.vtype', { dataset: { type } });
    switch (type) {
      case 'text':
        wrap.append(
          h(
            'div.vf-row',
            field('Placeholder', optionalText(q, 'placeholder', { name: 'placeholder' })),
            field('Min length', optionalNumber(q, 'minLength', { integer: true, min: 0, name: 'minLength' })),
            field('Max length', optionalNumber(q, 'maxLength', { integer: true, min: 1, name: 'maxLength' })),
          ),
          field('Pattern', optionalText(q, 'pattern', { placeholder: 'Regular expression, e.g. [A-Z]{2}\\d{4}', name: 'pattern' })),
        );
        break;
      case 'paragraph':
        wrap.append(
          h(
            'div.vf-row',
            field('Placeholder', optionalText(q, 'placeholder', { name: 'placeholder' })),
            field('Min length', optionalNumber(q, 'minLength', { integer: true, min: 0, name: 'minLength' })),
            field('Max length', optionalNumber(q, 'maxLength', { integer: true, min: 1, name: 'maxLength' })),
          ),
        );
        break;
      case 'email':
      case 'url':
        wrap.append(field('Placeholder', optionalText(q, 'placeholder', { name: 'placeholder' })));
        break;
      case 'number':
        wrap.append(
          h(
            'div.vf-row',
            field('Placeholder', optionalText(q, 'placeholder', { name: 'placeholder' })),
            field('Min', optionalNumber(q, 'min', { name: 'min' })),
            field('Max', optionalNumber(q, 'max', { name: 'max' })),
            field('Step', optionalNumber(q, 'step', { min: 0, name: 'step' })),
          ),
        );
        break;
      case 'date':
      case 'time':
        wrap.append(
          h(
            'div.vf-row',
            field('Earliest', optionalText(q, 'min', { type, name: 'min' })),
            field('Latest', optionalText(q, 'max', { type, name: 'max' })),
          ),
        );
        break;
      case 'radio':
      case 'checkbox':
      case 'select':
      case 'ranking':
        wrap.append(optionsEditor(pi, ii));
        break;
      case 'scale':
        wrap.append(
          h(
            'div.vf-row',
            field('From', requiredNumber(q, 'min', { min: -100, max: 100, fallback: 1, name: 'min' })),
            field('To', requiredNumber(q, 'max', { min: -100, max: 100, fallback: 5, name: 'max' })),
            field('Low label', optionalText(q, 'minLabel', { placeholder: 'e.g. Not likely', name: 'minLabel' })),
            field('High label', optionalText(q, 'maxLabel', { placeholder: 'e.g. Very likely', name: 'maxLabel' })),
          ),
          h('p.vf-hint', 'At most 11 steps.'),
        );
        break;
      case 'rating':
        wrap.append(field('Stars', requiredNumber(q, 'max', { min: 2, max: 10, fallback: 5, name: 'max' }), { hint: 'Between 2 and 10.' }));
        break;
      case 'grid':
      case 'checkbox-grid':
        wrap.append(
          h('div.vf-row', cellsEditor(pi, ii, 'rows', 'Rows', 'Row'), cellsEditor(pi, ii, 'columns', 'Columns', 'Column')),
        );
        break;
      case 'file': {
        const sizeInput = textInput(
          () => (q().maxSize !== undefined ? formatSize(q().maxSize) : ''),
          (v) => {
            const bytes = v.trim() === '' ? undefined : parseSize(v);
            if (bytes === undefined) delete q().maxSize;
            else q().maxSize = bytes;
          },
          { placeholder: 'e.g. 10MB', name: 'maxSize' },
        );
        wrap.append(
          h(
            'div.vf-row',
            field(
              'Accepted types',
              textInput(
                () => (q().accept || []).join(', '),
                (v) => {
                  const list = v.split(',').map((s) => s.trim()).filter(Boolean);
                  if (list.length) q().accept = list;
                  else delete q().accept;
                },
                { placeholder: '.pdf, image/*', name: 'accept' },
              ),
            ),
            field('Max size', sizeInput),
            field('Max files', optionalNumber(q, 'maxFiles', { integer: true, min: 1, max: 20, name: 'maxFiles' })),
          ),
        );
        break;
      }
      case 'phone': {
        const countrySelect = h('select.vf-select', { 'aria-label': 'Default country', dataset: { field: 'country' } });
        countrySelect.append(h('option', { value: '' }, 'None'));
        for (const { code, name } of COUNTRY_OPTIONS) {
          const dial = phoneCode(code);
          if (dial) countrySelect.append(h('option', { value: code, selected: code === q().country }, `${flagEmoji(code)} ${name} +${dial}`));
        }
        countrySelect.addEventListener('change', () => {
          if (countrySelect.value) q().country = countrySelect.value;
          else delete q().country;
          emit(false);
        });
        wrap.append(
          field('Placeholder', optionalText(q, 'placeholder', { name: 'placeholder' })),
          field('Default country', countrySelect, { hint: 'Preselected; respondents can pick another.' }),
        );
        break;
      }
      case 'address': {
        const countrySelect = h('select.vf-select', { 'aria-label': 'Fixed country', dataset: { field: 'country' } });
        countrySelect.append(h('option', { value: '' }, 'Any country'));
        for (const { code, name } of COUNTRY_OPTIONS) countrySelect.append(h('option', { value: code, selected: code === q().country }, name));
        countrySelect.addEventListener('change', () => {
          if (countrySelect.value) q().country = countrySelect.value;
          else delete q().country;
          emit(false);
        });
        wrap.append(field('Country', countrySelect, { hint: 'When set, respondents are not asked for a country.' }));
        break;
      }
      default:
        break;
    }
    return wrap;
  }

  function optionsEditor(pi, ii) {
    const q = () => form().pages[pi].items[ii];
    const type = q().type;
    const list = h('div.vopts', { role: 'list' });
    const page = form().pages[pi];

    const renderRows = () => {
      clear(list);
      q().options.forEach((_, oi) => list.append(optionRow(pi, ii, oi, page.id, renderRows)));
    };
    renderRows();

    const addBtn = h(
      'button.vbtn.vbtn--text',
      {
        type: 'button',
        onClick: () => {
          const opts = q().options;
          const used = new Set(opts.map((o) => o.label));
          let n = opts.length + 1;
          while (used.has(`Option ${n}`)) n++;
          opts.push({ label: `Option ${n}` });
          renderRows();
          emit(false);
          const last = list.lastElementChild?.querySelector('input[type="text"]');
          if (last) {
            last.focus();
            last.select();
          }
        },
      },
      icon('plus'),
      'Add option',
    );

    const extras = h('div.vf-row');
    if (type === 'radio' || type === 'checkbox') {
      const otherBox = checkbox(
        () => q().other !== undefined,
        (v) => {
          if (v) q().other = q().other ?? 'Other';
          else delete q().other;
          otherLabel.disabled = !v;
          if (v) otherLabel.value = q().other;
        },
        { name: 'hasOther' },
      );
      const otherLabel = textInput(
        () => q().other ?? 'Other',
        (v) => {
          q().other = v;
        },
        { name: 'other' },
      );
      otherLabel.disabled = q().other === undefined;
      extras.append(field('"Other" option with free text', otherBox, { inline: true }), field('Other label', otherLabel));
    }
    extras.append(
      field(
        'Shuffle options',
        checkbox(
          () => q().shuffle,
          (v) => {
            if (v) q().shuffle = true;
            else delete q().shuffle;
          },
          { name: 'shuffle' },
        ),
        { inline: true },
      ),
    );
    if (type === 'checkbox') {
      extras.append(
        field('Min selected', optionalNumber(q, 'minSelected', { integer: true, min: 0, name: 'minSelected' })),
        field('Max selected', optionalNumber(q, 'maxSelected', { integer: true, min: 1, name: 'maxSelected' })),
      );
    }

    return h('div.vopts-wrap', h('div.vf-label', 'Options'), list, addBtn, extras);
  }

  /** Parses a flags field ("muted, .positive") into a clean list. */
  function parseFlags(text) {
    return text
      .split(/[\s,]+/)
      .map((f) => f.replace(/^\./, '').replace(/[^\w-]/g, ''))
      .filter(Boolean);
  }

  /**
   * Optional "value" and "flags" for an option or grid row/column. Returns a
   * toggle button (for the row's tools) and a panel with the two inputs. The
   * panel starts open only when the item already has a value or flags. An
   * empty value is deleted so the label is used instead.
   */
  function choiceExtras(item, what) {
    const set = Boolean(item().value !== undefined || (item().flags && item().flags.length));
    const panel = h('div.vopt-extras', { hidden: !set });
    const value = h('input.vf-input', {
      type: 'text',
      value: item().value ?? '',
      placeholder: 'Stored value (defaults to label)',
      'aria-label': `${what} value`,
      dataset: { field: 'value' },
    });
    value.addEventListener('input', () => {
      const v = value.value.trim();
      if (v === '') delete item().value;
      else item().value = v;
      emit(false);
    });
    const flags = h('input.vf-input', {
      type: 'text',
      value: (item().flags ?? []).join(' '),
      placeholder: 'Flags, e.g. muted positive',
      'aria-label': `${what} flags`,
      dataset: { field: 'flags' },
    });
    flags.addEventListener('input', () => {
      const f = parseFlags(flags.value);
      if (f.length) item().flags = f;
      else delete item().flags;
      emit(false);
    });
    panel.append(value, flags);
    const toggle = h(
      'button.vbtn.vbtn--braces',
      { type: 'button', title: 'Value and flags', 'aria-label': `${what}: value and flags`, 'aria-expanded': String(set) },
      '{}',
    );
    toggle.classList.toggle('is-set', set);
    toggle.addEventListener('click', () => {
      panel.hidden = !panel.hidden;
      toggle.setAttribute('aria-expanded', String(!panel.hidden));
      if (!panel.hidden) value.focus();
    });
    return { toggle, panel };
  }

  /** A list editor for a grid's rows or columns (key = 'rows' | 'columns'). */
  function cellsEditor(pi, ii, key, title, noun) {
    const q = () => form().pages[pi].items[ii];
    const list = h('div.vopts', { role: 'list' });
    const renderRows = () => {
      clear(list);
      q()[key].forEach((_, ci) => list.append(cellRow(ci)));
    };
    const cellRow = (ci) => {
      const cell = () => q()[key][ci];
      const row = h('div.vopt', { role: 'listitem', dataset: { cells: key } });
      const label = h('input.vf-input', { type: 'text', value: cell().label, 'aria-label': `${noun} ${ci + 1} label` });
      label.addEventListener('input', () => {
        cell().label = label.value;
        emit(false);
      });
      label.addEventListener('keydown', (e) => {
        if (e.key === 'Enter') {
          e.preventDefault();
          q()[key].splice(ci + 1, 0, { label: '' });
          renderRows();
          emit(false);
          list.children[ci + 1]?.querySelector('input[type="text"]')?.focus();
        } else if (e.key === 'Backspace' && label.value === '' && q()[key].length > 1) {
          e.preventDefault();
          q()[key].splice(ci, 1);
          renderRows();
          emit(false);
          list.children[Math.max(0, ci - 1)]?.querySelector('input[type="text"]')?.focus();
        }
      });
      const { toggle, panel } = choiceExtras(cell, noun);
      row.append(
        label,
        h(
          'div.vopt-tools',
          toggle,
          iconButton('up', `Move ${noun.toLowerCase()} up`, () => {
            const arr = q()[key];
            if (ci === 0) return;
            [arr[ci - 1], arr[ci]] = [arr[ci], arr[ci - 1]];
            renderRows();
            emit(false);
          }),
          iconButton('down', `Move ${noun.toLowerCase()} down`, () => {
            const arr = q()[key];
            if (ci >= arr.length - 1) return;
            [arr[ci + 1], arr[ci]] = [arr[ci], arr[ci + 1]];
            renderRows();
            emit(false);
          }),
          iconButton('close', `Remove ${noun.toLowerCase()}`, () => {
            if (q()[key].length <= 1) return;
            q()[key].splice(ci, 1);
            renderRows();
            emit(false);
          }),
        ),
        panel,
      );
      return row;
    };
    renderRows();
    const addBtn = h(
      'button.vbtn.vbtn--text',
      {
        type: 'button',
        onClick: () => {
          const arr = q()[key];
          const used = new Set(arr.map((c) => c.label));
          let n = arr.length + 1;
          while (used.has(`${noun} ${n}`)) n++;
          arr.push({ label: `${noun} ${n}` });
          renderRows();
          emit(false);
          const last = list.lastElementChild?.querySelector('input[type="text"]');
          if (last) {
            last.focus();
            last.select();
          }
        },
      },
      icon('plus'),
      `Add ${noun.toLowerCase()}`,
    );
    return h('div.vopts-wrap', { dataset: { field: key } }, h('div.vf-label', title), list, addBtn);
  }

  function optionRow(pi, ii, oi, pageId, rerender) {
    const q = () => form().pages[pi].items[ii];
    const opt = () => q().options[oi];
    const type = q().type;
    const row = h('div.vopt', { role: 'listitem' });

    const def = h('input.vf-check', {
      type: type === 'checkbox' ? 'checkbox' : 'radio',
      name: `default-${pi}-${ii}`,
      checked: Boolean(opt().default),
      title: 'Selected by default',
      'aria-label': 'Selected by default',
    });
    def.addEventListener('click', () => {
      const wasDefault = Boolean(opt().default);
      if (type !== 'checkbox') {
        for (const o of q().options) delete o.default;
        if (wasDefault) def.checked = false;
        else opt().default = true;
      } else if (def.checked) opt().default = true;
      else delete opt().default;
      emit(false);
    });

    const label = h('input.vf-input', { type: 'text', value: opt().label, 'aria-label': `Option ${oi + 1} label` });
    label.addEventListener('input', () => {
      opt().label = label.value;
      emit(false);
    });
    label.addEventListener('keydown', (e) => {
      if (e.key === 'Enter') {
        e.preventDefault();
        q().options.splice(oi + 1, 0, { label: '' });
        rerender();
        emit(false);
        const next = row.parentElement?.children[oi + 1]?.querySelector('input[type="text"]');
        if (next) next.focus();
      } else if (e.key === 'Backspace' && label.value === '' && q().options.length > 1) {
        e.preventDefault();
        q().options.splice(oi, 1);
        rerender();
        emit(false);
        const prev = row.parentElement?.children[Math.max(0, oi - 1)]?.querySelector('input[type="text"]');
        if (prev) prev.focus();
      }
    });

    if (type === 'ranking') row.append(h('span.vopt-rank', { 'aria-hidden': 'true' }, String(oi + 1)), label);
    else row.append(def, label);

    if (type === 'radio' || type === 'select') {
      const goto = h('select.vf-select.vf-goto', { 'aria-label': 'After choosing this option' });
      for (const o of pageOptions(pageId, false)) goto.append(o);
      goto.value = opt().goto ?? '';
      goto.addEventListener('change', () => {
        if (goto.value) opt().goto = goto.value;
        else delete opt().goto;
        emit(false);
      });
      row.append(goto);
    }

    const { toggle, panel } = choiceExtras(opt, 'Option');
    row.append(
      h(
        'div.vopt-tools',
        toggle,
        iconButton('up', 'Move option up', () => {
          if (oi === 0) return;
          const opts = q().options;
          [opts[oi - 1], opts[oi]] = [opts[oi], opts[oi - 1]];
          rerender();
          emit(false);
        }),
        iconButton('down', 'Move option down', () => {
          const opts = q().options;
          if (oi >= opts.length - 1) return;
          [opts[oi + 1], opts[oi]] = [opts[oi], opts[oi + 1]];
          rerender();
          emit(false);
        }),
        iconButton('close', 'Remove option', () => {
          if (q().options.length <= 1) return;
          q().options.splice(oi, 1);
          rerender();
          emit(false);
        }),
      ),
      panel,
    );
    return row;
  }

  function contentCard(pi, ii) {
    const item = () => form().pages[pi].items[ii];
    const card = h('article.vcard.vcard--content', { dataset: { path: `${pi}.${ii}` } });
    card.append(
      h(
        'header.vcard-head',
        h('span.vcard-kind', icon('text'), 'Text'),
        h(
          'div.vcard-tools',
          iconButton('up', 'Move up', () => moveItem(pi, ii, -1)),
          iconButton('down', 'Move down', () => moveItem(pi, ii, 1)),
          iconButton('trash', 'Delete text', () => deleteItem(pi, ii), 'vbtn--danger'),
        ),
      ),
      h(
        'div.vcard-body',
        field(
          'Markdown',
          textarea(
            () => item().markdown,
            (v) => {
              item().markdown = v;
            },
            { rows: 3, placeholder: 'Any Markdown: headings, paragraphs, lists, links…', name: 'markdown' },
          ),
        ),
      ),
    );
    return card;
  }

  function pageCard(pi) {
    const page = () => form().pages[pi];
    const total = form().pages.length;
    const card = h('section.vpage', { dataset: { page: String(pi) }, 'aria-label': `Page ${pi + 1}` });

    const idInput = textInput(
      () => page().id,
      (v) => {
        const clean = v.trim();
        if (clean) renamePage(page(), clean);
      },
      { name: 'pageId' },
    );
    idInput.classList.add('vf-input--mono');
    const titleInput = textInput(
      () => page().title ?? '',
      (v) => {
        if (v.trim() === '') delete page().title;
        else page().title = v;
        maybeRederivePageId(page(), idInput, pi);
      },
      { placeholder: pi === 0 && !form().title ? 'Form title' : 'Page title (optional)', name: 'pageTitle' },
    );
    titleInput.classList.add('vf-input--title');

    const nextSelect = h('select.vf-select', { 'aria-label': 'After this page', dataset: { field: 'next' } });
    for (const o of pageOptions(page().id, true)) nextSelect.append(o);
    nextSelect.value = page().next ?? '';
    nextSelect.addEventListener('change', () => {
      if (nextSelect.value) page().next = nextSelect.value;
      else delete page().next;
      emit(false);
    });

    const head = h(
      'header.vpage-head',
      h('div.vpage-title', h('span.vpage-num', `Page ${pi + 1} of ${total}`), titleInput),
      h(
        'div.vpage-meta',
        field('After this page', nextSelect),
        field('Page id', idInput),
        h(
          'div.vcard-tools',
          iconButton('up', 'Move page up', () => movePage(pi, -1)),
          iconButton('down', 'Move page down', () => movePage(pi, 1)),
          iconButton('trash', 'Delete page', () => deletePage(pi), 'vbtn--danger'),
        ),
      ),
    );

    const items = h('div.vpage-items');
    page().items.forEach((item, ii) => items.append(item.type === 'content' ? contentCard(pi, ii) : questionCard(pi, ii)));
    if (!page().items.length) items.append(h('p.vpage-empty', 'This page is empty. Add a question or some text.'));

    card.append(head, items, addBar(pi));
    return card;
  }

  function addBar(pi) {
    const typeMenu = h('details.menu.vadd-menu');
    const summary = h('summary.vbtn.vbtn--text', icon('plus'), 'Add question', icon('chevron', 'icon-chevron'));
    const menu = h('div.menu-list', { role: 'menu' });
    for (const t of QUESTION_TYPES) {
      menu.append(
        h(
          'button.menu-item',
          {
            type: 'button',
            role: 'menuitem',
            onClick: () => addQuestion(pi, t),
          },
          typeLabel(t),
        ),
      );
    }
    typeMenu.append(summary, menu);
    return h(
      'div.vadd',
      typeMenu,
      h(
        'button.vbtn.vbtn--text',
        {
          type: 'button',
          onClick: () => {
            form().pages[pi].items.push({ type: 'content', markdown: 'Some text.' });
            emit(true);
            focusAfterRebuild(`${pi}.${form().pages[pi].items.length - 1}`, 'markdown');
          },
        },
        icon('plus'),
        'Add text',
      ),
    );
  }

  // --- Structural edits ------------------------------------------------------

  let pendingFocus = null;
  function focusAfterRebuild(path, fieldName) {
    pendingFocus = { path, fieldName };
  }

  function addQuestion(pi, type) {
    const page = form().pages[pi];
    const used = allQuestionIds();
    let n = 1;
    let label = 'Untitled question';
    while (used.has(slugify(`${label} ${n}`)) || used.has(slugify(label))) n++;
    if (used.has(slugify(label))) label = `${label} ${n}`;
    page.items.push(newQuestion(type, label, used));
    emit(true);
    focusAfterRebuild(`${pi}.${page.items.length - 1}`, 'label');
  }

  function moveItem(pi, ii, delta) {
    const pages = form().pages;
    const items = pages[pi].items;
    const target = ii + delta;
    if (target >= 0 && target < items.length) {
      [items[ii], items[target]] = [items[target], items[ii]];
      emit(true);
      focusAfterRebuild(`${pi}.${target}`, null);
      return;
    }
    // Across page boundaries.
    const targetPage = pi + delta;
    if (targetPage < 0 || targetPage >= pages.length) return;
    const [item] = items.splice(ii, 1);
    if (delta < 0) {
      pages[targetPage].items.push(item);
      focusAfterRebuild(`${targetPage}.${pages[targetPage].items.length - 1}`, null);
    } else {
      pages[targetPage].items.unshift(item);
      focusAfterRebuild(`${targetPage}.0`, null);
    }
    emit(true);
  }

  function duplicateItem(pi, ii) {
    const items = form().pages[pi].items;
    const copy = JSON.parse(JSON.stringify(items[ii]));
    if (copy.type !== 'content') {
      const used = allQuestionIds();
      copy.label = `${copy.label} (copy)`;
      copy.id = uniqueId(slugify(copy.label), used);
    }
    items.splice(ii + 1, 0, copy);
    emit(true);
    focusAfterRebuild(`${pi}.${ii + 1}`, 'label');
  }

  function deleteItem(pi, ii) {
    form().pages[pi].items.splice(ii, 1);
    emit(true);
  }

  function movePage(pi, delta) {
    const pages = form().pages;
    const target = pi + delta;
    if (target < 0 || target >= pages.length) return;
    [pages[pi], pages[target]] = [pages[target], pages[pi]];
    emit(true);
  }

  function deletePage(pi) {
    const pages = form().pages;
    if (pages.length === 1) {
      pages[0].items = [];
      delete pages[0].title;
      emit(true);
      return;
    }
    if (pages[pi].items.length && !confirm('Delete this page and everything on it?')) return;
    const removed = pages.splice(pi, 1)[0];
    for (const p of pages) {
      if (p.next === removed.id) delete p.next;
      for (const item of p.items) {
        if (item.type === 'content' || !CHOICE.has(item.type)) continue;
        for (const o of item.options) if (o.goto === removed.id) delete o.goto;
      }
    }
    emit(true);
  }

  function addPage() {
    const pages = form().pages;
    const used = allPageIds();
    pages.push({ id: uniqueId(`page-${pages.length + 1}`, used), items: [] });
    emit(true);
    requestAnimationFrame(() => root.querySelector('.vpage:last-of-type')?.scrollIntoView({ block: 'start', behavior: 'smooth' }));
  }

  // --- Settings card -------------------------------------------------------

  function settingsCard() {
    const f = () => form();
    const settings = () => {
      const s = f();
      if (!s.settings) s.settings = {};
      return s.settings;
    };
    const cleanSettings = () => {
      const s = f();
      if (s.settings && Object.keys(s.settings).length === 0) delete s.settings;
    };
    const card = h('section.vsettings', { 'aria-label': 'Form settings' });
    card.append(
      h('header.vcard-head', h('span.vcard-kind', icon('sliders'), 'Form')),
      h(
        'div.vcard-body',
        field(
          'Title',
          (() => {
            const i = textInput(
              () => f().title ?? '',
              (v) => {
                if (v.trim() === '') delete f().title;
                else f().title = v;
              },
              { placeholder: 'Untitled form', name: 'title' },
            );
            i.classList.add('vf-input--title');
            return i;
          })(),
        ),
        field(
          'Description',
          textarea(
            () => f().description ?? '',
            (v) => {
              if (v.trim() === '') delete f().description;
              else f().description = v;
            },
            { rows: 2, placeholder: 'Shown under the title (Markdown)', name: 'description' },
          ),
        ),
        h(
          'div.vf-row',
          field(
            'Submit button',
            textInput(
              () => f().settings?.submitLabel ?? '',
              (v) => {
                if (v.trim() === '') delete settings().submitLabel;
                else settings().submitLabel = v;
                cleanSettings();
              },
              { placeholder: 'Submit', name: 'submitLabel' },
            ),
          ),
          field(
            'Confirmation message',
            textInput(
              () => f().settings?.confirmation ?? '',
              (v) => {
                if (v.trim() === '') delete settings().confirmation;
                else settings().confirmation = v;
                cleanSettings();
              },
              { placeholder: 'Your response has been recorded.', name: 'confirmation' },
            ),
          ),
        ),
      ),
    );
    return card;
  }

  // --- Build ---------------------------------------------------------------

  function rebuild() {
    const active = document.activeElement;
    const activeCard = active && active.closest ? active.closest('[data-path]') : null;
    const restore = pendingFocus || (activeCard && root.contains(active) ? { path: activeCard.dataset.path, fieldName: active.dataset.field || null } : null);
    pendingFocus = null;
    const scrollTop = root.parentElement ? root.parentElement.scrollTop : 0;

    clear(root);
    root.append(settingsCard());
    form().pages.forEach((_, pi) => root.append(pageCard(pi)));
    root.append(
      h(
        'div.vadd.vadd--page',
        h('button.vbtn.vbtn--text', { type: 'button', onClick: addPage }, icon('page'), 'Add page'),
      ),
    );
    if (root.parentElement) root.parentElement.scrollTop = scrollTop;

    if (restore) {
      const card = root.querySelector(`[data-path="${restore.path}"]`);
      if (card) {
        const target = (restore.fieldName && card.querySelector(`[data-field="${restore.fieldName}"]`)) || card.querySelector('input, select, textarea');
        if (target) {
          target.focus({ preventScroll: true });
          if (pendingFocus === null && restore.fieldName === 'label' && target.select) target.select();
          card.scrollIntoView({ block: 'nearest' });
        }
      }
    }
  }

  return { el: root, rebuild };
}
